import { FunctionComponent, useEffect, useState } from "react";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { CompanyEnrichAutoCompleteItem } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";

const SUGGESTION_DEBOUNCE_MS = 400;

/**
 * Single free-text value with suggestions — the pattern the Maps create form
 * uses for country / city: the lookup only proposes known spellings, anything
 * typed stays usable.
 */
export const SuggestingTextField: FunctionComponent<{
	value: string;
	placeholder: string;
	fetchFn: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	error?: boolean;
	onChange: (value: string) => void;
}> = ({ value, placeholder, fetchFn, error, onChange }) => {
	const [searchValue, setSearchValue] = useState("");
	const [suggestions, setSuggestions] = useState<AutoCompleteItem[]>([]);

	useEffect(() => {
		const keyword = searchValue.trim();

		if (keyword.length < 2) {
			setSuggestions([]);

			return;
		}

		const handle = setTimeout(async () => {
			try {
				const results = await fetchFn(keyword);

				setSuggestions(results.map((entry) => ({ id: entry.id, title: entry.title })));
			} catch (err) {
				console.error(err);
			}
		}, SUGGESTION_DEBOUNCE_MS);

		return () => clearTimeout(handle);
	}, [searchValue, fetchFn]);

	const typed = searchValue.trim();
	const options = typed
		? [
				{ id: typed, title: typed },
				...suggestions.filter((suggestion) => suggestion.title !== typed),
			]
		: suggestions;

	return (
		<AutoComplete
			data={options}
			error={error}
			handleReset={() => {
				setSearchValue("");
				onChange("");
			}}
			handleSearch={setSearchValue}
			handleSelect={(item) => {
				onChange(item?.title ?? "");
				setSearchValue("");
			}}
			placeholder={placeholder}
			selectedItem={value}
		/>
	);
};
